import { createAsyncThunk } from '@reduxjs/toolkit';
import { WellFilterAPIResponse } from '../types/WellFilterTypes';
import api from './api';
import { createWrapperPayload, wrapperConstants } from './wrapper-api';

export async function fetchBusinessUnitsAPI(): Promise<WellFilterAPIResponse> {
  try {
    const response = await api.get(`/api/AssetStatus/filter-values/BusinessUnit`);
    return response.data;
  } catch (error) {
    console.error('Error fetching Business Units:', error);
    return { type: 'businessunit', values: [], id: '', dateCreated: '' };
  }
}

export async function fetchFieldsAPI(): Promise<WellFilterAPIResponse> {
  try {
    const response = await api.get(`/api/AssetStatus/filter-values/Field`);
    return response.data;
  } catch (error) {
    console.error('Error fetching Fields:', error);
    return { type: 'field', values: [], id: '', dateCreated: '' };
  }
}

export async function fetchMethodsOfProductionAPI(): Promise<WellFilterAPIResponse> {
  try {
    const response = await api.get(`/api/AssetStatus/filter-values/MethodOfProductionMapping`);
    return response.data;
  } catch (error) {
    console.error('Error fetching Methods of Production:', error);
    return { type: 'methodofproductionmapping', values: [], id: '', dateCreated: '' };
  }
}

export interface FilteredAssetListPayload {
  groupNames: string[];
  businessUnits: string[];
  fields: string[];
  methodsOfProduction: string[];
}

export const fetchFilteredAssetList = async (filters: FilteredAssetListPayload, wellGroupName?: string | null) => {
  try {
    return await api
      .post(`/Assets/FilterAssets?groupName=${wellGroupName ? wellGroupName : ''}`, filters, {
        headers: {
          'content-type': 'application/json',
        },
      })
      .then(function (response: any) {
        if (response) {
          return response.data;
        }
      });
  } catch (error) {
    console.error('Error fetching filtered asset list:', error);
    return [];
  }
};

export const fetchPCBusinessUnitsAPI = createAsyncThunk('globalFilters/fetchPCBusinessUnitsAPI', async () => {
  try {
    const requestPayload = createWrapperPayload('GET', `/pumpchecker/Lookup/GetBusinessUnits`);

    return await api.post(wrapperConstants.router, requestPayload).then(function (response) {
      if (response) {
        return response.data;
      } else {
        return undefined;
      }
    });
  } catch (error) {
    console.error('Error fetching PC Business Units:', error);
    return {};
  }
});

export const fetchPCAreasAPI = createAsyncThunk(
  'globalFilters/fetchPCAreasByBusinessUnitsAPI',
  async (businessUnitIds?: number[]) => {
    try {
      let params = '';
      if (businessUnitIds && businessUnitIds.length > 0) {
        params = businessUnitIds.map((id) => `businessUnitIds[]=${id}`).join('&');
      }
      const url = `/pumpchecker/Lookup/GetAreas${params ? '?' + params : ''}`;
      const requestPayload = createWrapperPayload('GET', url);
      return await api.post(wrapperConstants.router, requestPayload).then(function (response) {
        if (response) {
          return response.data;
        } else {
          return undefined;
        }
      });
    } catch (error) {
      console.error('Error fetching PC Areas:', error);
      return {};
    }
  },
);

export const fetchPCFieldsAPI = createAsyncThunk(
  'globalFilters/fetchPCFieldsByBusinessUnitsAndAreasAPI',
  async ({ businessUnitIds, areaIds }: { businessUnitIds?: number[]; areaIds?: number[] } = {}) => {
    try {
      let buParams = '';
      let areaParams = '';
      if (businessUnitIds && businessUnitIds.length > 0) {
        buParams = businessUnitIds.map((id) => `businessUnitIds[]=${id}`).join('&');
      }
      if (areaIds && areaIds.length > 0) {
        areaParams = areaIds.map((id) => `areaIds[]=${id}`).join('&');
      }
      let query = '';
      if (buParams && areaParams) {
        query = buParams + '&' + areaParams;
      } else if (buParams) {
        query = buParams;
      } else if (areaParams) {
        query = areaParams;
      }
      const url = `/pumpchecker/Lookup/GetFields${query ? '?' + query : ''}`;
      const requestPayload = createWrapperPayload('GET', url);
      return await api.post(wrapperConstants.router, requestPayload).then(function (response) {
        if (response) {
          return response.data;
        } else {
          return undefined;
        }
      });
    } catch (error) {
      console.error('Error fetching PC Fields', error);
      return {};
    }
  },
);

export const fetchPCWellGroups = createAsyncThunk('globalFilters/fetchPCWellGroups', async () => {
  try {
    const requestPayload = createWrapperPayload('GET', `/pumpchecker/Lookup/GetWellGroups`);

    return await api.post(wrapperConstants.router, requestPayload).then(function (response) {
      if (response) {
        return response.data;
      } else {
        return undefined;
      }
    });
  } catch (error) {
    console.error('Error fetching PC Well Groups:', error);
    return {};
  }
});

export const fetchPCWells = createAsyncThunk('globalFilters/fetchPCWells', async () => {
  try {
    const requestPayload = createWrapperPayload('GET', `/pumpchecker/Home/GetWells`);

    return await api.post(wrapperConstants.router, requestPayload).then(function (response) {
      if (response) {
        return response.data;
      } else {
        return undefined;
      }
    });
  } catch (error) {
    console.error('Error fetching PC Wells:', error);
    return {};
  }
});

export const fetchPCWellDetails = async (assetGuids: string[]) => {
  try {
    const requestPayload = createWrapperPayload('POST', '/pumpchecker/Well/GetWellDetails', assetGuids);
    return await api.post(wrapperConstants.router, requestPayload).then(function (response) {
      if (response) {
        return response.data;
      }
    });
  } catch (error) {
    console.error('Error fetching PC Well Details:', error);
    return [];
  }
};
